import { format, isToday } from 'date-fns';
import { GripVertical, Copy, Trash2, Plus } from 'lucide-react';

// Helper function to get job type color
const getJobTypeColor = (projectType) => {
  if (projectType === 'CON') {
    return { text: 'text-blue-400', border: '#3B82F6' }; // Blue for Construction
  }
  return { text: 'text-[#F5A623]', border: '#F5A623' }; // Orange for Developments (default)
};

// Helper function to format the project display
const formatProjectDisplay = (schedule) => {
  // Extract parts from project_ref: e.g., "LC1081-CON-Cherry Tree Lodge, Weston Haddon"
  const ref = schedule.project_ref || '';
  const parts = ref.split('-');
  
  if (parts.length >= 3) {
    const jobRef = parts[0]; // LC1081 or LUX210
    const jobType = parts[1]; // CON or DEV
    const jobName = parts.slice(2).join('-'); // Everything after the type
    
    return {
      line1: `${jobRef} – ${jobType}`,
      line2: jobName
    };
  }
  
  // Fallback to original display
  return {
    line1: ref.split(' - ')[0] || ref,
    line2: ref.split(' - ')[1] || ''
  };
};

export const ScheduleWeekView = ({
  days,
  schedules,
  canManageProjects,
  projectColorMap,
  employeeColorMap,
  PROJECT_COLORS,
  EMPLOYEE_COLORS,
  draggedSchedule,
  draggedProject,
  getSchedulesForDay,
  getInitials,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  onDuplicate,
  onDelete,
  onAddClick,
  onEdit,
  dupKeys,
}) => {
  // Either flavour of drag activates the dashed-amber drop-target style.
  const isDragging = Boolean(draggedSchedule || draggedProject);
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
      {days.map((day) => {
        const daySchedules = getSchedulesForDay(day);
        const isCurrentDay = isToday(day);
        const dayKey = format(day, 'yyyy-MM-dd');
        return (
          <div
            key={dayKey}
            data-testid={`week-cell-${dayKey}`}
            className={`min-h-[200px] rounded-lg border transition-colors ${
              isCurrentDay
                ? 'border-[#F5A623] bg-[#F5A623]/5'
                : isDragging
                  ? 'border-dashed border-[#F5A623]/50 bg-[#F5A623]/5'
                  : 'border-white/10 bg-[#2A3240]'
            }`}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, day)}
          >
            <div className={`p-3 border-b ${isCurrentDay ? 'border-[#F5A623]/30' : 'border-white/10'}`}>
              <div className="text-xs text-gray-500 uppercase">{format(day, 'EEE')}</div>
              <div className={`text-lg font-bold ${isCurrentDay ? 'text-[#F5A623]' : 'text-white'}`}>{format(day, 'd')}</div>
            </div>
            <div className="p-2 space-y-2">
              {daySchedules.map((schedule) => {
                const jobTypeColor = getJobTypeColor(schedule.project_type);
                const projectDisplay = formatProjectDisplay(schedule);
                
                return (
                  <div key={schedule.id} draggable={canManageProjects} onDragStart={(e) => onDragStart(e, schedule)}
                    onDragEnd={onDragEnd}
                    onDoubleClick={(e) => { e.stopPropagation(); onEdit && onEdit(schedule); }}
                    title="Double-click to edit"
                    className={`p-2 rounded border-l-4 transition-all group cursor-move bg-[#1E2530] border border-white/10 ${draggedSchedule?.id === schedule.id ? 'opacity-50' : ''}`}
                    style={{ borderLeftColor: jobTypeColor.border }}>
                    <div className="flex items-start justify-between gap-1">
                      <div className="flex-1 min-w-0">
                        {canManageProjects && <GripVertical className="w-3 h-3 text-gray-500 mb-1 cursor-grab" />}
                        {/* Job Reference and Type - colored by job type */}
                        <p className={`text-xs font-semibold truncate ${jobTypeColor.text}`}>
                          {projectDisplay.line1}
                        </p>
                        {/* Job Name/Location */}
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {projectDisplay.line2}
                        </p>
                      </div>
                      {canManageProjects && (
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); onDuplicate(schedule, day); }}
                            className="p-1 text-gray-400 hover:text-[#F5A623]"><Copy className="w-3 h-3" /></button>
                          <button onClick={(e) => { e.stopPropagation(); onDelete(schedule.id, e); }}
                            className="p-1 text-gray-400 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      )}
                    </div>
                    {/* Employee full names */}
                    <div className="mt-1.5 space-y-0.5">
                      {schedule.employees.map(emp => {
                        const empColor = employeeColorMap[emp.id] || EMPLOYEE_COLORS[0];
                        const dup = dupKeys?.has(`${schedule.date}|${emp.id}`);
                        return (
                          <div key={emp.id} className={`px-1.5 py-0.5 rounded text-[10px] font-medium truncate flex items-center gap-1 ${empColor.bg} ${empColor.text} border ${empColor.border}`}>
                            <span className="truncate">{emp.name}</span>
                            {dup && <span className="shrink-0 px-1 rounded-full bg-red-500/30 text-red-200 border border-red-400/40 text-[9px] font-semibold" data-testid={`double-booked-${schedule.id}-${emp.id}`}>2 jobs</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {canManageProjects && (
                <button onClick={() => onAddClick(day)}
                  className="w-full p-2 border border-dashed border-white/20 rounded text-gray-500 hover:border-[#F5A623] hover:text-[#F5A623] flex items-center justify-center gap-1">
                  <Plus className="w-3 h-3" /><span className="text-xs">Add</span>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
